import * as React from 'react'

import { ReadAloudContent } from '../../../Shared/components/readAloud/readAloudContent'
import { EXTRA_SMALL_ICON_BASE_SRC, SMALL_ICON_BASE_SRC, } from '../../../Shared/utils/constants'
import {
  CONJUNCTION_PURPOSES,
  ConjunctionSpecificTip,
  ConjunctionSpecificTipRow,
  annotatedResponse,
  conjunctionSpecificTipRow,
  withEmphasis,
} from '../../libs/conjunctionSpecificTips'

const DIRECTIONS_LABEL = 'Read the directions aloud'

const plusIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/plus.svg`
const dashIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/dash.svg`
const lightbulbIconSrc = `${SMALL_ICON_BASE_SRC}/lightbulb.svg`

const ConjunctionTip = ({ id, row, }: { id: string, row: ConjunctionSpecificTipRow }) => (
  <div className="conjunction-tip" id={id}>
    <div className="conjunction-tip-section">
      <div className="conjunction-tip-heading">
        <img alt="" src={lightbulbIconSrc} />
        <h3>Use <em>{row.conjunction}</em> {CONJUNCTION_PURPOSES[row.conjunction]}</h3>
      </div>
      <p className="conjunction-tip-sentence">
        {row.stem} <span className="conjunction-tip-conjunction">{row.conjunction}</span> <br />
        {annotatedResponse(row).map(({ text, annotation, }, index) => (
          annotation ? <span className={`annotation ${annotation}`} key={index}>{text}</span> : <React.Fragment key={index}>{text}</React.Fragment>
        ))}
      </p>
    </div>
    <div className="conjunction-tip-section">
      <h4>What this sentence does</h4>
      <dl className="conjunction-tip-key">
        <dt><span className="annotation core">{row.core_label}</span>:</dt>
        <dd>{withEmphasis(row.core_description)}</dd>
        <dt><span className="annotation detail">{row.detail_label}</span>:</dt>
        <dd>{withEmphasis(row.detail_description)}</dd>
      </dl>
    </div>
  </div>
)

interface InstructionBoxProps {
  conjunction: string;
  exampleOpen: boolean;
  onToggleExample: () => void;
  tip?: ConjunctionSpecificTip;
}

const InstructionBox = ({ conjunction, exampleOpen, onToggleExample, tip, }: InstructionBoxProps) => {
  const exampleId = React.useId()
  const tipRow = conjunctionSpecificTipRow(tip, conjunction)
  const showExample = exampleOpen && !!tipRow

  return (
    <section className="instruction-box">
      <div className="instruction-box-directions">
        <ReadAloudContent label={DIRECTIONS_LABEL}>
          <div className="instruction-box-text">
            <div className="instruction-box-heading">
              <h2>Write just ONE sentence that is:</h2>
            </div>
            <ul>
              <li>Based on what you read</li>
              <li>In your own words</li>
              <li>Clear, specific, and detailed</li>
              <li className="sr-only">Screenreader users, please use your tab key to locate the text editor.</li>
            </ul>
          </div>
        </ReadAloudContent>
        {tipRow && (
          <button
            aria-controls={showExample ? exampleId : undefined}
            aria-expanded={showExample}
            className="quill-button outlined grey medium focus-on-light"
            onClick={onToggleExample}
            type="button"
          >
            <img alt="" src={showExample ? dashIconSrc : plusIconSrc} />
            {showExample ? <p>Hide example</p> : <p>See example</p>}
          </button>
        )}
      </div>
      {showExample && <ConjunctionTip id={exampleId} row={tipRow} />}
    </section>
  )
}

export default InstructionBox
